import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../components/ButtonUI";
import { userAtom } from "../../../state/auth";
import SelectComp from "../../../components/Select";
import { StudySelectedIdAtom } from "../../../state/studySelectedIdAtom";
import { getAllPatientByStudyId, getStudyForms } from "../../../api/study";
import { buildExportFilters, exportFormat, ExportScope } from "./exportFilters";

const KINDS = [
  { value: "visits", label: "missingWording.exportVisits" },
  { value: "epro", label: "missingWording.exportEpro" },
  { value: "events", label: "missingWording.exportEvents" },
  { value: "queries", label: "missingWording.exportQueries" },
];

const toggle = (list: string[], value: string) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

type Props = {
  setOpenModal: React.Dispatch<React.SetStateAction<string>>;
  downloadApi: any;
};

export const DownloadModal: React.FC<Props | any> = ({
  setOpenModal,
  downloadApi,
}) => {
  const [user] = useAtom(userAtom);
  const [studySelectedId, setStudySelectedId] = useAtom(StudySelectedIdAtom);
  const [shouldUpdate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [extention, setExtention] = useState("");
  const [scope, setScope] = useState<ExportScope>("all");
  const [kinds, setKinds] = useState<string[]>(["visits", "epro", "events"]);
  const [participants, setParticipants] = useState<any[]>([]);
  const [forms, setForms] = useState<any[]>([]);
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const [formIds, setFormIds] = useState<string[]>([]);
  const { t } = useTranslation() as any;
  const format = exportFormat(scope, extention);
  const _handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const filters = buildExportFilters({
        scope,
        kinds,
        participantIds,
        formIds,
      });
      await downloadApi(studySelectedId, format, filters);
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {}, [shouldUpdate]);
  useEffect(() => {
    if (!studySelectedId || scope === "all") return;
    getAllPatientByStudyId(studySelectedId)
      .then((list) => setParticipants(list || []))
      .catch(console.error);
    getStudyForms(studySelectedId)
      .then((list) => setForms(list || []))
      .catch(console.error);
  }, [studySelectedId, scope]);
  return (
    <div className="px-6 ">
      <h1 className="mb-4  ">{t("missingWording.extractData")}</h1>
      <h3 className="mb-8 ">{t("missingWording.extractDataDescrp")}</h3>
      <div className="mb-6">
        <p className="font-semibold mb-2">{t("missingWording.exportScope")}</p>
        {[
          ["all", "missingWording.exportScopeAll"],
          ["selection", "missingWording.exportScopeSelection"],
          ["adverse", "missingWording.exportScopeAdverse"],
        ].map(([value, label]) => (
          <label key={value} className="flex items-center gap-x-2 mb-1">
            <input
              type="radio"
              name="exportScope"
              checked={scope === value}
              onChange={() => setScope(value as ExportScope)}
            />
            {t(label)}
          </label>
        ))}
      </div>

      {scope === "selection" && (
        <div className="mb-6">
          <p className="font-semibold mb-2">{t("missingWording.exportKinds")}</p>
          <div className="flex gap-x-4 flex-wrap">
            {KINDS.map((kind) => (
              <label key={kind.value} className="flex items-center gap-x-1">
                <input
                  type="checkbox"
                  checked={kinds.includes(kind.value)}
                  onChange={() => setKinds(toggle(kinds, kind.value))}
                />
                {t(kind.label)}
              </label>
            ))}
          </div>
          {kinds.includes("visits") && forms.length > 0 && (
            <>
              <p className="font-semibold mt-4 mb-2">
                {t("missingWording.exportForms")}
              </p>
              <div className="max-h-32 overflow-y-auto">
                {forms.map((form: any) => (
                  <label key={form._id} className="flex items-center gap-x-1">
                    <input
                      type="checkbox"
                      checked={formIds.includes(form._id)}
                      onChange={() => setFormIds(toggle(formIds, form._id))}
                    />
                    {form.name}
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {scope !== "all" && participants.length > 0 && (
        <div className="mb-6">
          <p className="font-semibold mb-2">
            {t("missingWording.exportParticipants")}
          </p>
          <div className="max-h-32 overflow-y-auto grid grid-cols-3">
            {participants.map((participant: any) => (
              <label key={participant._id} className="flex items-center gap-x-1">
                <input
                  type="checkbox"
                  checked={participantIds.includes(participant._id)}
                  onChange={() =>
                    setParticipantIds(toggle(participantIds, participant._id))
                  }
                />
                {participant.publicId}
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex  mb-16 items-center justify-around  gap-x-4 ">
        <SelectComp
          onChange={(e) => {
            setExtention(e.target.value);
            e.preventDefault();
            e.stopPropagation();
            return;
          }}
          className="w-72 text-center"
          labelProps={{
            className: "text-base font-semibold text-gray-400 -mb-1",
          }}
          value={format}
          disabled={scope === "adverse"}
          labelText={""}
          text={"Download type "}
          options={[
            { label: "XLSX", value: "XLSX" },
            // { label: "CSV", value: "CSV" },
            { label: "PDF", value: "PDF" },
          ]}
        />
      </div>

      <Button
        status="secondary"
        width="100px"
        height="50px"
        className="float-left btn-delete"
        onClick={() => setOpenModal(false)}
      >
        {t("Participant.Cancel")}
      </Button>

      <Button
        status="primary"
        width="100px"
        height="50px"
        className="float-right"
        type="submit"
        disabled={!format || format === "" || (scope === "selection" && !kinds.length)}
        onClick={() => {
          if (isSubmitting) {
            return;
          }

          _handleSubmit();
        }}
      >
        {t("missingWording.confirm")}
      </Button>
    </div>

    // </ChakraProvider>
  );
};

export default DownloadModal;
